/**
 * Brand directory router
 * @package @app/api
 *
 * US-023 expansion: extends `list` with filters (industry, sport, deal size
 * range), sorting, and returns parsed JSON array fields. Adds `get` returning
 * the full brand profile for the detail page.
 */

import { ORPCError } from '@orpc/server';
import { parseJsonArray, parseJsonObjectArray } from '@app/validators/schemas';
import { z } from 'zod';
import { adminProcedure, protectedProcedure } from '../middleware/auth';

// JSON-string array fields stored on Brand rows. Parsed at the router
// boundary so the client works with real arrays.
const BRAND_ARRAY_FIELDS = [
  'coreValues',
  'sportsFocus',
  'dealTypes',
  'featuredAthletes',
  'categories',
  'targetSports',
] as const;

type BrandArrayField = (typeof BRAND_ARRAY_FIELDS)[number];

// biome-ignore lint/suspicious/noExplicitAny: prisma types are overly narrow here
function parseBrand(brand: any) {
  return {
    ...brand,
    avgDealSize: brand.avgDealSize ? Number(brand.avgDealSize) : null,
    dealRangeMin: brand.dealRangeMin ? Number(brand.dealRangeMin) : null,
    dealRangeMax: brand.dealRangeMax ? Number(brand.dealRangeMax) : null,
    coreValues: parseJsonArray(brand.coreValues),
    sportsFocus: parseJsonArray(brand.sportsFocus),
    dealTypes: parseJsonArray(brand.dealTypes),
    categories: parseJsonArray(brand.categories),
    targetSports: parseJsonArray(brand.targetSports),
    featuredAthletes: parseJsonObjectArray<{ name: string; sport: string; school: string }>(
      brand.featuredAthletes,
    ),
  };
}

const listInputSchema = z.object({
  search: z.string().optional(),
  industry: z.string().optional(),
  sport: z.string().optional(), // matches anything in sportsFocus / targetSports
  dealSizeMin: z.number().nonnegative().optional(),
  dealSizeMax: z.number().nonnegative().optional(),
  sortBy: z.enum(['totalDeals', 'name', 'dealRangeMax']).default('totalDeals'),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
});

export const brandRouter = {
  list: protectedProcedure.input(listInputSchema).handler(async ({ input, context }) => {
    try {
    const { db } = context;

    // Postgres-side filters for indexed / typed columns.
    const where: Record<string, unknown> = {};

    if (input.search) {
      // Match brand name OR industry OR mission (case-insensitive).
      where.OR = [
        { name: { contains: input.search, mode: 'insensitive' } },
        { industry: { contains: input.search, mode: 'insensitive' } },
        { mission: { contains: input.search, mode: 'insensitive' } },
        { description: { contains: input.search, mode: 'insensitive' } },
      ];
    }
    if (input.industry) {
      where.industry = input.industry;
    }

    // Deal-size range matches brands whose [dealRangeMin, dealRangeMax] intersects
    // the requested [dealSizeMin, dealSizeMax]. Using dealRangeMax only is a
    // reasonable approximation — brands that top out below the athlete's minimum
    // are filtered out.
    if (input.dealSizeMin !== undefined) {
      where.dealRangeMax = { gte: input.dealSizeMin };
    }

    // Sort mapping.
    const orderBy =
      input.sortBy === 'name'
        ? { name: 'asc' as const }
        : input.sortBy === 'dealRangeMax'
          ? { dealRangeMax: 'desc' as const }
          : { totalDeals: 'desc' as const };

    let [rawBrands, total] = await Promise.all([
      db.brand.findMany({
        where,
        orderBy,
        skip: (input.page - 1) * input.limit,
        take: input.limit,
      }),
      db.brand.count({ where }),
    ]);

    // Auto-seed reference brand data on first request when table is empty.
    if (total === 0 && !input.search && !input.industry && input.dealSizeMin === undefined) {
      try {
        const { seedBrandsIfEmpty } = await import('@app/db');
        const seeded = await seedBrandsIfEmpty();
        if (seeded) {
          [rawBrands, total] = await Promise.all([
            db.brand.findMany({
              where,
              orderBy,
              skip: (input.page - 1) * input.limit,
              take: input.limit,
            }),
            db.brand.count({ where }),
          ]);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[brand.list] auto-seed failed:', message);
        throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `Brand seed failed: ${message}` });
      }
    }

    // Sport filter is applied post-DB since sportsFocus is a JSON string.
    // Volume is small (<1K brands) so client-side filtering of the page is
    // fine; if this grows, denormalize into a relational join table.
    let brands = rawBrands.map(parseBrand);
    if (input.sport) {
      const sport = input.sport;
      brands = brands.filter(
        (b) =>
          b.sportsFocus.includes(sport) ||
          b.targetSports.includes(sport),
      );
    }

    return {
      brands,
      total,
      page: input.page,
      limit: input.limit,
    };
    } catch (err) {
      if (err instanceof ORPCError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `Brand list failed: ${message}` });
    }
  }),

  get: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ input, context }) => {
      const brand = await context.db.brand.findUnique({ where: { id: input.id } });
      if (!brand) throw new Error('Brand not found');
      return parseBrand(brand);
    }),

  create: adminProcedure
    .input(
      z.object({
        name: z.string().min(1),
        website: z.string().url().optional(),
        industry: z.string().optional(),
        description: z.string().optional(),
        contactEmail: z.string().email().optional(),
        contactName: z.string().optional(),
        targetSports: z.array(z.string()).optional(),
        targetFollowers: z.number().int().positive().optional(),
      }),
    )
    .handler(async ({ input, context }) => {
      const { createId } = await import('@app/db');
      return context.db.brand.create({
        data: {
          id: createId(),
          ...input,
          targetSports: input.targetSports ? JSON.stringify(input.targetSports) : undefined,
        },
      });
    }),
};

// Exposed for tests / other routers (e.g., match router in PR-D).
export { parseBrand, BRAND_ARRAY_FIELDS };
export type BrandArrayFieldName = BrandArrayField;
